import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { API_BASE_URL } from "../../utils/apiConfig";
import type { ChatMessage, ChatPresence } from "../../services/teamChatService";

let activeClient: Client | null = null;
let reconnectDelayMs = 1000;

export function disconnectTeamChat(): void {
    if (activeClient) {
        try {
            activeClient.deactivate();
        } catch {
            // ignore teardown errors
        }
        activeClient = null;
    }
    reconnectDelayMs = 1000;
}

export function sendTeamChatMessage(conversationId: number, body: string): boolean {
    if (!activeClient || !activeClient.connected) {
        return false;
    }
    activeClient.publish({
        destination: `/app/conversations/${conversationId}/messages`,
        body: JSON.stringify({ body }),
    });
    return true;
}

export function isTeamChatConnected(): boolean {
    return Boolean(activeClient?.connected);
}

export function connectTeamChat(handlers: {
    onMessage: (message: ChatMessage) => void;
    onPresence?: (presence: ChatPresence) => void;
    onError: (userMessage: string) => void;
    onConnectionChange?: (connected: boolean) => void;
}): () => void {
    disconnectTeamChat();

    const client = new Client({
        webSocketFactory: () => new SockJS(`${API_BASE_URL}/ws`) as WebSocket,
        reconnectDelay: reconnectDelayMs,
        heartbeatIncoming: 25000,
        heartbeatOutgoing: 25000,
        debug: () => {
            // Never log STOMP frames — they include message bodies.
        },
        onConnect: () => {
            reconnectDelayMs = 1000;
            client.reconnectDelay = reconnectDelayMs;
            handlers.onConnectionChange?.(true);
            client.subscribe("/user/queue/messages", (frame) => {
                try {
                    const parsed = JSON.parse(frame.body) as ChatMessage;
                    handlers.onMessage(parsed);
                } catch {
                    handlers.onError("Unable to read a new message.");
                }
            });
            client.subscribe("/user/queue/errors", (frame) => {
                try {
                    const parsed = JSON.parse(frame.body) as { message?: string };
                    handlers.onError(parsed.message || "Unable to send message. Please try again.");
                } catch {
                    handlers.onError("Unable to send message. Please try again.");
                }
            });
            client.subscribe("/user/queue/presence", (frame) => {
                try {
                    const parsed = JSON.parse(frame.body) as ChatPresence;
                    if (typeof parsed.userId === "number") {
                        handlers.onPresence?.(parsed);
                    }
                } catch {
                    // Ignore a malformed presence frame; the last known state stays.
                }
            });
        },
        onWebSocketClose: () => {
            handlers.onConnectionChange?.(false);
            reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
            client.reconnectDelay = reconnectDelayMs;
        },
        onStompError: () => {
            handlers.onError("Chat is temporarily unavailable. Please try again.");
        },
    });

    activeClient = client;
    client.activate();

    return () => {
        disconnectTeamChat();
    };
}
